// components/AdminDashboard.js - Complete version with hierarchical admin system and super admin user management
import React, { useState, useEffect } from 'react';
import AdminStatistics from './AdminStatistics';
import Statistics from './Statistics';
import './AdminDashboard.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const AdminDashboard = ({ onBack, user, onImpersonate }) => {
  const [managedUsers, setManagedUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('users'); // 'users', 'statistics', 'team-stats'
  const [activeTab, setActiveTab] = useState('managed'); // 'managed', 'all', or 'manage-users'
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    fetchManagedUsers();
    fetchAllUsers();
    checkSuperAdmin();
  }, []);

  const checkSuperAdmin = async () => {
    try {
      const response = await fetch(`${API_URL}/api/super-admin/check/`, {
        headers: {
          'X-User-ID': user.userId,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setIsSuperAdmin(data.is_super_admin);
      }
    } catch (error) {
      console.error('Error checking super admin status:', error);
    }
  };

  const fetchManagedUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/managed-users/`, {
        headers: {
          'X-User-ID': user.userId,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setManagedUsers(data);
      }
    } catch (error) {
      console.error('Error fetching managed users:', error);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/all-users/`, {
        headers: {
          'X-User-ID': user.userId,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setAllUsers(data);
      }
    } catch (error) {
      console.error('Error fetching all users:', error);
    } finally {
      setLoading(false);
    }
  };

  const claimUser = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/claim-user/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-ID': user.userId,
        },
        body: JSON.stringify({ user_id: userId }),
      });
      if (response.ok) {
        fetchManagedUsers();
        fetchAllUsers();
      }
    } catch (error) {
      console.error('Error claiming user:', error);
    }
  };

  const releaseUser = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/release-user/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-ID': user.userId,
        },
        body: JSON.stringify({ user_id: userId }),
      });
      if (response.ok) {
        fetchManagedUsers();
        fetchAllUsers();
      }
    } catch (error) {
      console.error('Error releasing user:', error);
    }
  };

  const promoteUser = async (userId) => {
    setActionMessage('');
    try {
      const response = await fetch(`${API_URL}/api/super-admin/promote/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-ID': user.userId,
        },
        body: JSON.stringify({ user_id: userId }),
      });
      const data = await response.json();
      if (response.ok) {
        setActionMessage(data.message);
        fetchAllUsers();
      } else {
        setActionMessage(data.error || 'Error promoting user');
      }
    } catch (error) {
      setActionMessage('Error promoting user');
      console.error('Error promoting user:', error);
    }
  };

  const demoteUser = async (userId) => {
    setActionMessage('');
    try {
      const response = await fetch(`${API_URL}/api/super-admin/demote/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-ID': user.userId,
        },
        body: JSON.stringify({ user_id: userId }),
      });
      const data = await response.json();
      if (response.ok) {
        setActionMessage(data.message);
        fetchAllUsers();
        fetchManagedUsers();
      } else {
        setActionMessage(data.error || 'Error demoting user');
      }
    } catch (error) {
      setActionMessage('Error demoting user');
      console.error('Error demoting user:', error);
    }
  };

  const impersonateUser = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/super-admin/impersonate/${userId}/`, {
        method: 'POST',
        headers: { 'X-User-ID': user.userId },
      });
      const data = await response.json();
      if (response.ok && data.session_token) {
        onImpersonate(data);
        onBack();
      } else {
        setActionMessage(data.error || 'Error switching user');
      }
    } catch (error) {
      setActionMessage('Error switching user');
    }
  };

  const resetPin = async (userId, username) => {
    setActionMessage('');
    try {
      const response = await fetch(`${API_URL}/api/admin/reset-pin/${userId}/`, {
        method: 'POST',
        headers: { 'X-User-ID': user.userId },
      });
      const data = await response.json();
      if (response.ok) {
        setActionMessage(`PIN cleared for ${username}. They will set a new one on next login.`);
      } else {
        setActionMessage(data.error || 'Error resetting PIN');
      }
    } catch (error) {
      setActionMessage('Error resetting PIN');
    }
  };

  const superAdminResetPin = async (userId, username) => {
    setActionMessage('');
    try {
      const response = await fetch(`${API_URL}/api/super-admin/reset-pin/${userId}/`, {
        method: 'POST',
        headers: { 'X-User-ID': user.userId },
      });
      const data = await response.json();
      if (response.ok) {
        setActionMessage(`PIN cleared for ${username}. They will set a new one on next login.`);
      } else {
        setActionMessage(data.error || 'Error resetting PIN');
      }
    } catch (error) {
      setActionMessage('Error resetting PIN');
    }
  };

  const deleteUser = async (userId) => {
    setActionMessage('');
    try {
      const response = await fetch(`${API_URL}/api/super-admin/delete/${userId}/`, {
        method: 'DELETE',
        headers: {
          'X-User-ID': user.userId,
        },
      });
      const data = await response.json();
      if (response.ok) {
        setActionMessage(`${data.message} (${data.logs_deleted} logs deleted)`);
        setConfirmDelete(null);
        fetchAllUsers();
        fetchManagedUsers();
      } else {
        setActionMessage(data.error || 'Error deleting user');
      }
    } catch (error) {
      setActionMessage('Error deleting user');
      console.error('Error deleting user:', error);
    }
  };

  // Handle different views
  if (view === 'team-stats') {
    return (
      <AdminStatistics
        onBack={() => setView('users')}
        user={user}
      />
    );
  }

  if (view === 'statistics' && selectedUser) {
    return (
      <Statistics
        onBack={() => setView('users')}
        user={user}
        forUserId={selectedUser.id}
        adminTitle={`Statistics for ${selectedUser.username}`}
      />
    );
  }

  const renderManagedUsers = () => (
    <div className="users-table">
      <table>
        <thead>
          <tr>
            <th>Username</th>
            <th>Full Name</th>
            <th>Email</th>
            <th>Points This Month</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {managedUsers.map(worker => (
            <tr key={worker.id}>
              <td>
                {worker.username}
                {worker.is_superuser && <span className="admin-badge">Admin</span>}
              </td>
              <td>
                {worker.profile ?
                  `${worker.profile.first_name} ${worker.profile.last_name}`.trim() || 'Not set'
                  : 'Not set'
                }
              </td>
              <td>{worker.profile?.email || 'Not set'}</td>
              <td>{worker.total_points_this_month}</td>
              <td>
                <div className="action-buttons">
                  <button
                    onClick={() => {
                      setSelectedUser(worker);
                      setView('statistics');
                    }}
                    className="view-stats-button"
                  >
                    View Stats
                  </button>
                  <button
                    onClick={() => releaseUser(worker.id)}
                    className="release-button"
                  >
                    Release
                  </button>
                  <button
                    onClick={() => resetPin(worker.id, worker.username)}
                    className="reset-pin-button"
                  >
                    Reset PIN
                  </button>
                </div>
              </td>
            </tr>
          ))}
          {managedUsers.length === 0 && (
            <tr>
              <td colSpan="5" className="no-data">No workers assigned to you</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const renderAllUsers = () => (
    <div className="users-table">
      <table>
        <thead>
          <tr>
            <th>Username</th>
            <th>Full Name</th>
            <th>Email</th>
            <th>Claimed By</th>
            <th>Points This Month</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {allUsers.map(userItem => {
            const isClaimedByMe = userItem.manager?.id === user.id;
            const isUnclaimed = !userItem.manager;
            const isAdmin = userItem.is_superuser;
            const isCurrentUser = userItem.id === user.id;

            return (
              <tr key={userItem.id} className={isAdmin ? 'admin-row' : ''}>
                <td>
                  {userItem.username}
                  {isAdmin && <span className="admin-badge">Admin</span>}
                  {isCurrentUser && <span className="current-user-badge">You</span>}
                </td>
                <td>
                  {userItem.profile ?
                    `${userItem.profile.first_name} ${userItem.profile.last_name}`.trim() || 'Not set'
                    : 'Not set'
                  }
                </td>
                <td>{userItem.profile?.email || 'Not set'}</td>
                <td>
                  {isCurrentUser ? 'N/A (Current User)' :
                   userItem.manager ? userItem.manager.username : 'Unclaimed'}
                </td>
                <td>{userItem.total_points_this_month || 0}</td>
                <td>
                  {!isCurrentUser && (
                    <div className="action-buttons">
                      {(isSuperAdmin || user.is_director) && !userItem.is_super_admin && (
                        <button
                          onClick={() => impersonateUser(userItem.id)}
                          className="impersonate-button"
                        >
                          Login as
                        </button>
                      )}
                      {isClaimedByMe && (
                        <>
                          <button
                            onClick={() => {
                              setSelectedUser(userItem);
                              setView('statistics');
                            }}
                            className="view-stats-button"
                          >
                            View Stats
                          </button>
                          <button
                            onClick={() => releaseUser(userItem.id)}
                            className="release-button"
                          >
                            Release
                          </button>
                        </>
                      )}
                      {isUnclaimed && (
                        <button
                          onClick={() => claimUser(userItem.id)}
                          className="claim-button"
                        >
                          Claim {isAdmin ? 'Admin' : 'User'}
                        </button>
                      )}
                      {!isClaimedByMe && !isUnclaimed && (
                        <span className="claimed-by-other">
                          Claimed by {userItem.manager.username}
                        </span>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderUserManagement = () => {
    // Get super admin usernames (hardcoded to match backend)
    const superAdminUsernames = ['acorvino', 'rcarballo'];
    const iAmAcorvino = (user.username || '').toLowerCase() === 'acorvino';

    return (
      <div className="user-management">
        {actionMessage && (
          <div className={`action-message ${actionMessage.includes('Error') ? 'error' : 'success'}`}>
            {actionMessage}
          </div>
        )}

        {confirmDelete && (
          <div className="confirm-delete-modal">
            <div className="confirm-delete-content">
              <h3>Confirm Delete</h3>
              <p>Are you sure you want to delete user <strong>{confirmDelete.username}</strong>?</p>
              <p className="warning-text">
                This will permanently delete their account and all {confirmDelete.total_points_this_month > 0 ? 'activity logs' : 'data'}.
              </p>
              <div className="confirm-buttons">
                <button onClick={() => setConfirmDelete(null)} className="cancel-button">
                  Cancel
                </button>
                <button onClick={() => deleteUser(confirmDelete.id)} className="confirm-delete-button">
                  Delete User
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="users-table">
          <table>
            <thead>
              <tr>
                <th>Username</th>
                <th>Full Name</th>
                <th>Status</th>
                <th>Activity</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {allUsers.map(userItem => {
                const isAdmin = userItem.is_superuser;
                const isCurrentUser = userItem.id === user.id;
                const isSuperAdminUser = superAdminUsernames.includes(userItem.username.toLowerCase());
                const hasActivity = userItem.total_points_this_month > 0;

                return (
                  <tr key={userItem.id} className={isAdmin ? 'admin-row' : ''}>
                    <td>
                      {userItem.username}
                      {isSuperAdminUser && <span className="super-admin-badge">Super Admin</span>}
                      {isAdmin && !isSuperAdminUser && <span className="admin-badge">Admin</span>}
                      {isCurrentUser && <span className="current-user-badge">You</span>}
                    </td>
                    <td>
                      {userItem.profile ?
                        `${userItem.profile.first_name} ${userItem.profile.last_name}`.trim() || 'Not set'
                        : 'Not set'
                      }
                    </td>
                    <td>
                      {isSuperAdminUser ? 'Super Admin' : isAdmin ? 'Admin' : 'User'}
                    </td>
                    <td>
                      {userItem.total_points_this_month > 0 ?
                        `${userItem.total_points_this_month} pts this month` :
                        <span className="no-activity">No activity</span>
                      }
                    </td>
                    <td>
                      {!isCurrentUser && !isSuperAdminUser && (
                        <div className="action-buttons">
                          {isAdmin ? (
                            <button
                              onClick={() => demoteUser(userItem.id)}
                              className="demote-button"
                            >
                              Demote
                            </button>
                          ) : (
                            <button
                              onClick={() => promoteUser(userItem.id)}
                              className="promote-button"
                            >
                              Promote to Admin
                            </button>
                          )}
                          <button
                            onClick={() => setConfirmDelete(userItem)}
                            className="delete-button"
                          >
                            Delete
                          </button>
                          {/* acorvino: reset anyone. rcarballo: reset admins only */}
                          {(iAmAcorvino || isAdmin) && (
                            <button
                              onClick={() => superAdminResetPin(userItem.id, userItem.username)}
                              className="reset-pin-button"
                            >
                              Reset PIN
                            </button>
                          )}
                          {isSuperAdmin && (
                            <button
                              onClick={() => impersonateUser(userItem.id)}
                              className="impersonate-button"
                            >
                              Login as
                            </button>
                          )}
                        </div>
                      )}
                      {isSuperAdminUser && !isCurrentUser && (
                        <div className="action-buttons">
                          <span className="protected-user">Protected</span>
                          {/* acorvino can reset rcarballo's PIN and vice versa isn't allowed;
                              acorvino can reset any super admin except themselves */}
                          {iAmAcorvino && (
                            <button
                              onClick={() => superAdminResetPin(userItem.id, userItem.username)}
                              className="reset-pin-button"
                            >
                              Reset PIN
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="admin-dashboard">
      <div className="header">
        <button onClick={onBack} className="back-button">← Back to Menu</button>
        <h2>Admin Dashboard</h2>
        <button
          onClick={() => setView('team-stats')}
          className="team-stats-button"
        >
          Team Statistics
        </button>
      </div>

      <div className="admin-content">
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'managed' ? 'active' : ''}`}
            onClick={() => setActiveTab('managed')}
          >
            My Workers ({managedUsers.length})
          </button>
          <button
            className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All Users ({allUsers.length})
          </button>
          {isSuperAdmin && (
            <button
              className={`tab-button super-admin-tab ${activeTab === 'manage-users' ? 'active' : ''}`}
              onClick={() => setActiveTab('manage-users')}
            >
              User Management
            </button>
          )}
        </div>

        {loading ? (
          <div className="loading">Loading users...</div>
        ) : (
          <>
            {activeTab === 'managed' && (
              <>
                <h3>Your Workers</h3>
                {renderManagedUsers()}
              </>
            )}
            {activeTab === 'all' && (
              <>
                <h3>All Users & Claim Status</h3>
                {renderAllUsers()}
              </>
            )}
            {activeTab === 'manage-users' && isSuperAdmin && (
              <>
                <h3>User Management</h3>
                <p className="management-note">Promote users to admin, demote admins, or delete unused accounts.</p>
                {renderUserManagement()}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
